import {
  CloseRoundLight,
  DoneLight,
  EditLightBig,
  FileFill,
  LightningFill,
  RoadDocLightCustom,
  RoadFillCustom,
} from '@profgeosoft-ui/icons';
import clsx from 'clsx';
import { format, secondsToMilliseconds } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { MouseEvent, useRef } from 'react';
import BulldozerIcon from 'src/shared/assets/icons/bulldozer.svg';
import { IconButton } from 'src/shared/components/icon-button';
import { PadPreparation } from 'src/store/well-pad-preparation/entities';

import styles from './pad-collapse-header.module.scss';

type Props = {
  pad: PadPreparation;
  isEditing: boolean;
  isEditingBlocked: boolean;
  isEditModeAvailable: boolean;
  onEdit(id: number): void;
  onCancel: VoidFunction;
  onApply: VoidFunction;
};

const dateFormat = 'dd.MM.yy';

export const PadCollapseHeader = observer<Props>(function PadCollapseHeader({
  pad,
  isEditingBlocked,
  isEditModeAvailable,
  isEditing,
  onEdit,
  onCancel,
  onApply,
}) {
  const buttonsWrapperRef = useRef<HTMLDivElement | null>(null);

  const {
    padReadiness: { padReady, landReady, routeReady, routeDocumentationReady, electricityReady },
    constantValues: { padName, fieldName, blockName },
    values: {
      landLeaseDate,
      forestProjectDate,
      routeReadyDate: _routeReadyDate,
      routeDocumentationDate: _routeDocumentationDate,
      electricityDate: _electricityDate,
    },
    firstRse,
  } = pad;

  const padReadyDate: string = !firstRse
    ? '-'
    : format(secondsToMilliseconds(firstRse.values.padReadyDate!), dateFormat);
  const landDate: string =
    !landLeaseDate && !forestProjectDate
      ? '-'
      : format(secondsToMilliseconds(Math.max(landLeaseDate ?? 0, forestProjectDate ?? 0)), dateFormat);
  const routeReadyDate: string = !_routeReadyDate ? '-' : format(secondsToMilliseconds(_routeReadyDate), dateFormat);
  const routeDocumentationDate: string = !_routeDocumentationDate
    ? '-'
    : format(secondsToMilliseconds(_routeDocumentationDate), dateFormat);
  const electricityDate: string = !_electricityDate ? '-' : format(secondsToMilliseconds(_electricityDate), dateFormat);

  const handleClick = (e: MouseEvent) => {
    if (buttonsWrapperRef.current?.contains(e.target as Node)) {
      e.stopPropagation();
    }

    onEdit(pad.id);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.textInfo}>
        <p className={styles.padName}>{padName}</p>
        <div className={styles.padDetails}>
          <p>{blockName}</p>
          <p>{fieldName}</p>
        </div>
      </div>

      <div className={styles.indicatorsWrapper}>
        <div className={styles.indicatorWrapper}>
          <BulldozerIcon className={clsx(styles.icon, styles[padReady])} />
          <p>{padReadyDate}</p>
        </div>
        <div className={styles.indicatorWrapper}>
          <FileFill className={clsx(styles.icon, styles[landReady])} />
          <p>{landDate}</p>
        </div>
        <div className={styles.indicatorWrapper}>
          <RoadFillCustom className={clsx(styles.icon, styles[routeReady])} />
          <p>{routeReadyDate}</p>
        </div>
        <div className={styles.indicatorWrapper}>
          <RoadDocLightCustom className={clsx(styles.icon, styles[routeDocumentationReady])} />
          <p>{routeDocumentationDate}</p>
        </div>
        <div className={styles.indicatorWrapper}>
          <LightningFill className={clsx(styles.icon, styles[electricityReady])} />
          <p>{electricityDate}</p>
        </div>

        <div ref={buttonsWrapperRef} className={styles.buttonsWrapper}>
          {!isEditing && isEditModeAvailable && (
            <IconButton
              isDisabled={isEditingBlocked}
              icon={<EditLightBig className={styles.editIcon} />}
              onClick={handleClick}
            />
          )}
          {isEditing && (
            <>
              <IconButton
                className={clsx(styles.editModeButton, styles.editModeButton_cancel)}
                icon={<CloseRoundLight className={styles.editModeButton__icon} />}
                onClick={onCancel}
              />
              <IconButton
                className={clsx(styles.editModeButton, styles.editModeButton_done)}
                icon={<DoneLight className={styles.editModeButton__icon} />}
                onClick={onApply}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
});
